/**
 * Validation primitives shared by server and client (SEC-4).
 *
 * The server treats these as the source of truth; the client reuses them so a
 * form cannot accept something the API will reject. Frontend validation is UX
 * convenience, backend validation is the rule (AP-1).
 */

import { z } from 'zod';

import { DEFAULT_PAGE_SIZE, DISPLAY_TIMEZONE, MAX_PAGE_SIZE, TAG_PATTERN } from './constants.js';
import { isValidMoneyString, toMoney } from './money.js';

/**
 * A positive money amount as a string (MONEY-2).
 *
 * Numbers are rejected outright: accepting a JSON number here would let a
 * client send 0.1 + 0.2 = 0.30000000000000004 into a DECIMAL column and quietly
 * undo everything money.ts exists to prevent.
 */
/**
 * Checks run in one pass rather than as a chain of `.refine` calls.
 *
 * Zod does not stop at the first failing refinement in a chain, so a chained
 * version would call `toMoney("abc")` after the format check had already
 * failed — and `toMoney` throws rather than returning false, which surfaces as
 * an unhandled exception instead of a validation error.
 */
const checkMoney = (value: string, ctx: z.RefinementCtx, { allowZero }: { allowZero: boolean }): void => {
  if (!isValidMoneyString(value)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Amount must be a number with at most 2 decimal places',
    });
    return; // Nothing below can run safely on an unparseable value.
  }

  const amount = toMoney(value);

  // Range is already bounded by isValidMoneyString, which rejects anything
  // outside DECIMAL(14,2) — so only the sign needs checking here.
  if (allowZero ? amount.isNegative() : !amount.greaterThan(0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: allowZero ? 'Amount must not be negative' : 'Amount must be greater than zero',
    });
  }
};

export const moneyString = z
  .string({ required_error: 'Amount is required', invalid_type_error: 'Amount must be sent as a string, not a number' })
  .trim()
  .min(1, 'Amount is required')
  .superRefine((value, ctx) => checkMoney(value, ctx, { allowZero: false }));

/** Like `moneyString` but allows zero — for salvage values, advances and opening balances. */
export const moneyStringOrZero = z
  .string({ invalid_type_error: 'Amount must be sent as a string, not a number' })
  .trim()
  .superRefine((value, ctx) => checkMoney(value, ctx, { allowZero: true }));

/** A non-negative quantity: feed in kg, milk in litres. Up to 2 decimal places. */
export const quantityString = z
  .string()
  .trim()
  .regex(/^\d+(\.\d{1,2})?$/, 'Quantity must be a non-negative number with at most 2 decimal places')
  .refine((value) => Number.parseFloat(value) > 0, 'Quantity must be greater than zero');

/** §7 — dates cross the API as YYYY-MM-DD. */
export const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format')
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00Z`);
    // Round-trip guards against 2026-02-31, which Date silently rolls forward.
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  }, 'Not a real calendar date');

/**
 * Today in Asia/Dhaka as `YYYY-MM-DD` (AP-7, NFR-9).
 *
 * Not `new Date().toISOString()`. A `txn_date` is a calendar day, not an
 * instant, and which day it is depends on where the partner is standing: at
 * 00:30 Dhaka on 11 August it is still 10 August in UTC. Comparing a Dhaka date
 * against a UTC one rejected every entry made between midnight and 6 a.m. —
 * a milking-shed hour — as "in the future".
 *
 * `Intl` rather than a date library so this stays usable from both the server
 * (no dayjs) and the client. `en-CA` formats in exactly YYYY-MM-DD.
 */
const dhakaDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: DISPLAY_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export const todayInDhaka = (now: Date = new Date()): string => dhakaDateFormatter.format(now);

/** A date that cannot be in the future — a transaction cannot be paid tomorrow. */
export const pastOrTodayDate = isoDate.refine((value) => value <= todayInDhaka(), 'Date cannot be in the future');

/**
 * Bangladeshi mobile number, which is the login identifier (P0-BR2).
 *
 * Accepts 01XXXXXXXXX, +8801XXXXXXXXX and 8801XXXXXXXXX, normalising to the
 * local 11-digit form so the same person cannot be created twice under two
 * spellings of the same number.
 */
export const phoneBD = z
  .string()
  .trim()
  .transform((value) => value.replace(/[\s-]/g, ''))
  .refine((value) => /^(?:\+?88)?01[3-9]\d{8}$/.test(value), 'Enter a valid Bangladeshi mobile number (01XXXXXXXXX)')
  .transform((value) => value.replace(/^\+?88/, ''));

/** BR-12 — G-001 / C-001. */
export const animalTag = z
  .string()
  .trim()
  .toUpperCase()
  .regex(TAG_PATTERN, 'Tag must look like G-001 (goat) or C-001 (cattle)');

/**
 * AP-3 — every list endpoint is paginated.
 *
 * The maximum is a resource-exhaustion guard (SEC-4): without it, `?limit=100000`
 * is a one-request denial of service against a free-tier host.
 */
export const pagination = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
});
export type Pagination = z.infer<typeof pagination>;

/** Inclusive date range for reports and ledgers. */
export const dateRange = z
  .object({ from: isoDate, to: isoDate })
  .refine((value) => value.from <= value.to, { message: '"from" must not be after "to"', path: ['from'] });

/** A positive integer database id arriving as a path or query string. */
export const id = z.coerce.number().int().positive('Invalid id');

/** Free text with a length cap, so a note cannot be used to bloat the database. */
export const note = (max = 1000) => z.string().trim().max(max, `Must be ${max} characters or fewer`);

/** A required reason — void, reject and cancellation all demand one (BR-08). */
export const reason = z
  .string()
  .trim()
  .min(3, 'Please give a reason')
  .max(500, 'Reason must be 500 characters or fewer');

/**
 * A URL restricted to safe schemes (SEC-4).
 *
 * Used for the CCTV links in settings (P4-T07): a stored `javascript:` URL that
 * the app renders as a link is stored XSS against every partner.
 */
export const safeUrl = z
  .string()
  .trim()
  .url('Must be a valid URL')
  .refine((value) => {
    try {
      return ['http:', 'https:', 'rtsp:', 'rtsps:'].includes(new URL(value).protocol);
    } catch {
      return false;
    }
  }, 'URL must use http, https or rtsp');

/**
 * Password policy for partner accounts.
 *
 * Length carries most of the strength; a composition rule that forces "Passw0rd!"
 * buys little. 10 characters is the floor, and the obvious choices are refused.
 */
export const password = z
  .string()
  .min(10, 'Password must be at least 10 characters')
  .max(128, 'Password must be 128 characters or fewer')
  .refine(
    (value) => !['password', '12345678', 'agrofarm', 'qwerty'].some((weak) => value.toLowerCase().includes(weak)),
    'Password is too easy to guess',
  );

/** Builds a Zod enum from one of the const objects in enums.ts. */
export const enumOf = <T extends Record<string, string>>(obj: T) =>
  z.enum(Object.values(obj) as [T[keyof T], ...T[keyof T][]]);
